import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    HeartHandshake, Plus, CreditCard, MapPin, Clock, DollarSign,
    CheckCircle2, XCircle, Calendar, User as UserIcon, Package,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CareOpsPanel from "@/components/CareOpsPanel";
import HomeHealthBookingForm from "@/components/HomeHealthBookingForm";
import FamilyPaymentDialog from "@/components/FamilyPaymentDialog";

export const FAMILY = {
    dashboard: "family-dashboard",
    setupPaymentBtn: "family-setup-payment-btn",
    paymentStatusBadge: "family-payment-status-badge",
    newRequestBtn: "family-new-request-btn",
    requestRow: "family-request-row",
    cancelRequestBtn: "family-request-cancel-btn",
};

export default function FamilyDashboard() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [requests, setRequests] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [signupCtx, setSignupCtx] = useState({ service: "", state: user?.family_state || "" });
    const [payOpen, setPayOpen] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/care/requests");
            setRequests(data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    // Prefill from signup context (localStorage) or family_state fallback.
    useEffect(() => {
        try {
            const ctx = JSON.parse(localStorage.getItem("careco_signup_context") || "null");
            if (ctx && ctx.pillar === "home-health") {
                setSignupCtx({
                    service: ctx.service || "",
                    state: ctx.state || (user?.family_state || ""),
                });
                return;
            }
        } catch { /* noop */ }
        // No signup context — still seed state from the family's profile
        if (user?.family_state) {
            setSignupCtx((prev) => ({ ...prev, state: prev.state || user.family_state }));
        }
    }, [user?.family_state]);

    async function setupPayment() {
        setPayOpen(true);
    }

    async function cancelRequest(id) {
        if (!window.confirm("Cancel this care request?")) return;
        try {
            await api.delete(`/care/requests/${id}`);
            toast.success("Cancelled");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    }

    const openCount = requests.filter((r) => r.status === "open" || r.status === "matched").length;
    const activeCount = requests.filter((r) => r.status === "in_progress").length;

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid={FAMILY.dashboard} className="max-w-7xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Family portal</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        Hello, {user?.full_name?.split(" ")[0]}
                    </h1>
                    {user?.care_recipient_name && (
                        <p className="text-sm text-stone-500 mt-1 font-dmsans">
                            Caring for <span className="font-medium text-stone-800">{user.care_recipient_name}</span>
                        </p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Badge
                            data-testid={FAMILY.paymentStatusBadge}
                            className={user?.payment_method_on_file
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                : "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-50"}
                        >
                            <CreditCard className="w-3.5 h-3.5 mr-1" />
                            Payment method: {user?.payment_method_on_file ? "On file" : "Not set"}
                        </Badge>
                    </div>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <StatCard icon={Calendar} label="Open requests" value={openCount} accent="emerald" />
                    <StatCard icon={CheckCircle2} label="Care in progress" value={activeCount} accent="sky" />
                    <StatCard icon={HeartHandshake} label="Care recipient" value={user?.care_recipient_name || "—"} accent="orange" small />
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left: primary action */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card className="rounded-2xl border-stone-200 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white overflow-hidden relative grain-overlay">
                            <CardContent className="p-8 relative z-10">
                                <HeartHandshake className="w-10 h-10 text-emerald-200 mb-4" strokeWidth={1.5} />
                                <h2 className="font-outfit text-2xl sm:text-3xl font-semibold">
                                    Book care for your loved one
                                </h2>
                                <p className="text-emerald-100/90 font-dmsans mt-2 max-w-lg">
                                    Post what you need — service, state, hours, and budget — and vetted caregivers with the right credentials will match to you.
                                </p>
                                <Button
                                    data-testid={FAMILY.newRequestBtn}
                                    onClick={() => setShowForm((v) => !v)}
                                    className="mt-6 rounded-full bg-white text-emerald-800 hover:bg-emerald-50 px-6"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    {showForm ? "Close form" : "Post a care request"}
                                </Button>
                            </CardContent>
                        </Card>

                        {showForm && (
                            <Card className="rounded-2xl border-stone-200">
                                <CardContent className="p-6">
                                    <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-1">
                                        Care request details
                                    </h3>
                                    <p className="text-sm text-stone-500 font-dmsans mb-5">
                                        Pick a package, or build your own service mix à la carte. You&rsquo;ll get a live weekly estimate.
                                    </p>
                                    <HomeHealthBookingForm
                                        initialState={signupCtx.state}
                                        initialServiceId={signupCtx.service}
                                        onSuccess={() => { setShowForm(false); load(); }}
                                    />
                                </CardContent>
                            </Card>
                        )}

                        <Card className="rounded-2xl border-stone-200">
                            <CardContent className="p-6">
                                <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-4">Your care requests</h3>
                                <div className="space-y-3">
                                    {requests.length === 0 && (
                                        <p className="text-sm text-stone-500 font-dmsans">No requests yet. Post one above.</p>
                                    )}
                                    {requests.map((r) => (
                                        <div
                                            key={r.id}
                                            data-testid={FAMILY.requestRow}
                                            className="p-4 border border-stone-200 rounded-xl hover:bg-stone-50 transition-colors"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-outfit text-base font-semibold text-stone-900">
                                                            {r.package_label
                                                                ? `${r.package_label} package`
                                                                : (r.services?.length
                                                                    ? `${r.services.length} service${r.services.length > 1 ? "s" : ""}`
                                                                    : r.service_label)}
                                                        </span>
                                                        {r.package_id && (
                                                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50 text-[10px]">
                                                                <Package className="w-3 h-3 mr-1" /> Package
                                                            </Badge>
                                                        )}
                                                        <RequestStatusBadge status={r.status} />
                                                    </div>
                                                    {r.services?.length > 0 && (
                                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                            {r.services.map((s) => (
                                                                <span
                                                                    key={s.service_id}
                                                                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 font-dmsans"
                                                                >
                                                                    {s.service_label} · {s.hours_per_week}h
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-stone-500 font-dmsans">
                                                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.state}</span>
                                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.hours_per_week} h/wk</span>
                                                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${((r.weekly_total_cents || r.hourly_budget_cents * r.hours_per_week || 0) / 100).toFixed(0)}/wk</span>
                                                        {r.required_credential && (
                                                            <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> {r.required_credential}</span>
                                                        )}
                                                    </div>
                                                    {r.notes && (
                                                        <div className="mt-2 text-xs text-stone-600 font-dmsans line-clamp-2">{r.notes}</div>
                                                    )}
                                                </div>
                                                {(r.status === "open" || r.status === "matched") && (
                                                    <Button
                                                        data-testid={FAMILY.cancelRequestBtn}
                                                        aria-label="Cancel care request"
                                                        title="Cancel this request"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => cancelRequest(r.id)}
                                                        className="text-red-600 hover:bg-red-50 hover:text-red-700 h-8"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                        <span className="ml-1 text-xs font-outfit">Cancel</span>
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <CareOpsPanel
                            role="family"
                            careRequestId={requests.find((r) => r.status === "matched" || r.status === "in_progress")?.id || requests[0]?.id}
                        />
                    </div>

                    {/* Right: payment method card */}
                    <div className="space-y-6">
                        <FamilyAddressCard />

                        <Card className="rounded-2xl border-stone-200">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <CreditCard className="w-5 h-5 text-emerald-700" />
                                    <h3 className="font-outfit text-lg font-semibold text-stone-900">Payment method</h3>
                                </div>
                                <p className="text-sm text-stone-500 font-dmsans mb-4">
                                    We charge for care only when a caregiver has been matched and confirmed. Add a card to unlock instant booking.
                                </p>
                                {user?.payment_method_on_file ? (
                                    <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                                        <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                                            <CheckCircle2 className="w-4 h-4" /> Payment method on file
                                        </div>
                                        <p className="text-xs text-emerald-700/80 mt-1">
                                            {user.stripe_customer_id?.startsWith("cus_mock") && "(dev mode — no real card charged)"}
                                        </p>
                                    </div>
                                ) : (
                                    <Button
                                        data-testid={FAMILY.setupPaymentBtn}
                                        onClick={setupPayment}
                                        disabled={busy}
                                        className="w-full rounded-full bg-stone-900 hover:bg-stone-800 text-white h-11"
                                    >
                                        Add payment method
                                    </Button>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="rounded-2xl border-stone-200">
                            <CardContent className="p-6">
                                <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-3">Trust & safety</h3>
                                <ul className="space-y-2 text-sm text-stone-600 font-dmsans">
                                    <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Background-checked caregivers</li>
                                    <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Credential verification (RN/LPN/CNA/HHA)</li>
                                    <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Encrypted document vault for care records</li>
                                    <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> Cancel any request before care starts</li>
                                </ul>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
            <FamilyPaymentDialog
                open={payOpen}
                onOpenChange={setPayOpen}
                onSuccess={async () => { await refresh(); }}
            />
        </div>
    );
}

function StatCard({ icon: Icon, label, value, accent, small }) {
    const accents = {
        emerald: "bg-emerald-50 text-emerald-700",
        sky: "bg-sky-50 text-sky-700",
        orange: "bg-orange-50 text-orange-700",
    };
    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6 flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accents[accent]}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-0.5">{label}</div>
                    <div className={`font-outfit font-semibold text-stone-900 ${small ? "text-lg truncate" : "text-2xl"}`}>{value}</div>
                </div>
            </CardContent>
        </Card>
    );
}

function RequestStatusBadge({ status }) {
    const map = {
        open: "bg-orange-50 text-orange-700 border-orange-100",
        matched: "bg-emerald-50 text-emerald-700 border-emerald-100",
        in_progress: "bg-sky-50 text-sky-700 border-sky-100",
        completed: "bg-stone-100 text-stone-700 border-stone-200",
        cancelled: "bg-red-50 text-red-700 border-red-100",
    };
    return (
        <Badge className={`${map[status] || map.open} hover:opacity-100 text-xs`}>
            {status.replace("_", " ")}
        </Badge>
    );
}

function FamilyAddressCard() {
    const { user, refresh } = useAuth();
    const [form, setForm] = React.useState({
        family_address: user?.family_address || "",
        family_address_lat: user?.family_address_lat || "",
        family_address_lng: user?.family_address_lng || "",
    });
    const [busy, setBusy] = React.useState(false);

    async function useMyLocation() {
        try {
            const pos = await new Promise((res, rej) =>
                navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })
            );
            setForm({ ...form, family_address_lat: pos.coords.latitude, family_address_lng: pos.coords.longitude });
            toast.success("Coordinates captured");
        } catch { toast.error("Could not get location"); }
    }

    async function save() {
        setBusy(true);
        try {
            await api.put("/evv/family-address", {
                family_address: form.family_address || null,
                family_address_lat: form.family_address_lat ? parseFloat(form.family_address_lat) : null,
                family_address_lng: form.family_address_lng ? parseFloat(form.family_address_lng) : null,
            });
            toast.success("Address saved");
            await refresh();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    }

    return (
        <Card className="rounded-2xl border-stone-200">
            <CardContent className="p-6">
                <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-emerald-700" /> Care location (EVV)
                </h3>
                <p className="text-xs text-stone-500 font-dmsans mb-4">
                    Where care will be provided. Caregivers must clock in within 300m for a compliant visit.
                </p>
                <Input
                    data-testid="family-address-input"
                    value={form.family_address}
                    onChange={(e) => setForm({ ...form, family_address: e.target.value })}
                    placeholder="123 Main St, Brooklyn NY"
                    className="h-11 rounded-xl mb-2"
                />
                <div className="grid grid-cols-2 gap-2">
                    <Input
                        data-testid="family-lat-input"
                        value={form.family_address_lat}
                        onChange={(e) => setForm({ ...form, family_address_lat: e.target.value })}
                        placeholder="Latitude"
                        className="h-11 rounded-xl"
                    />
                    <Input
                        data-testid="family-lng-input"
                        value={form.family_address_lng}
                        onChange={(e) => setForm({ ...form, family_address_lng: e.target.value })}
                        placeholder="Longitude"
                        className="h-11 rounded-xl"
                    />
                </div>
                <div className="mt-3 flex gap-2">
                    <Button onClick={useMyLocation} variant="outline" className="rounded-full h-10 flex-1">
                        <MapPin className="w-4 h-4 mr-1.5" /> Use my location
                    </Button>
                    <Button
                        data-testid="family-address-save"
                        onClick={save}
                        disabled={busy}
                        className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-10 flex-1"
                    >
                        {busy ? "..." : "Save"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
